/**
 * `growth/admob-provider` — **native AdMob** backend of `AdProvider`.
 *
 * It is the piece Melodimals contributes to the portfolio kit: the canon ships `none`
 * (no-op), `adsense` (web) and `fake` (tests), but not a mobile backend. Here it lives,
 * wrapping `@melodimals/admob-api` — the vendored fork of
 * `tauri-plugin-google-admob` to which `maxAdContentRating` was added in
 * `initialize` precisely to be able to comply with the child regime.
 *
 * ── Regime K (§6 of the strategy) ────────────────────────────────────────────
 * `init()` forces the three COPPA/Families flags **at the SDK level**, not
 * in the account configuration: `tagForChildDirectedTreatment`,
 * `tagForUnderAgeOfConsent` and `maxAdContentRating: "G"`. It is what makes
 * the declaration in `docs/store/DATA_SAFETY.md` true, so they are not optional
 * parameters: if the caller asks for another rating, it is ignored (see `admobInitOptions`,
 * which composes the kit's hardening with the native backend's own floor).
 *
 * ── Lazy loading ─────────────────────────────────────────────────────────────
 * The SDK enters via dynamic `import()` INSIDE `init()`, and the factory only calls
 * `init()` when the config says the ads are live. With the
 * flags off not a byte is downloaded: the privacy promise is technical.
 */

import { hardenAdInitOptions, strictestAdContentRating } from "./config";
import type {
  AdContentRating,
  AdEvent,
  AdEventName,
  AdInitOptions,
  AdKind,
  AdProvider,
  AdsConfig,
  AdShowResult,
  AdTier,
} from "./types";
import { AD_TIER_REGIMES } from "./types";

/** Backend id in the factory and in telemetry. */
export const AD_NETWORK_ADMOB = "admob";

/**
 * Formats this backend serves. `native` is left out on purpose: the native
 * format requires laying out the creative inside the UI, and an ad card
 * disguised as content inside an app for kids aged 2–6 is exactly the
 * kind of deceptive ad that the Families Policy prohibits.
 */
const SUPPORTED_KINDS: ReadonlySet<AdKind> = new Set(["banner", "interstitial", "rewarded"]);

/**
 * Google TEST ad units. They are the default on purpose: without a real
 * AdMob account (human gate, see `docs/store/RELEASE_STORES.md` → G8) it is preferable
 * to serve test inventory than to send traffic to an invented id.
 */
export const ADMOB_TEST_AD_UNITS = {
  banner: "ca-app-pub-3940256099942544/9214589741",
  interstitial: "ca-app-pub-3940256099942544/1033173712",
  rewarded: "ca-app-pub-3940256099942544/5224354917",
} as const;

/** Subset of `@melodimals/admob-api` used by this backend. */
export interface AdMobSdk {
  initialize(options: {
    tagForChildDirectedTreatment?: boolean;
    tagForUnderAgeOfConsent?: boolean;
    maxAdContentRating?: AdContentRating;
    testDeviceIds?: string[];
  }): Promise<{ initialized: boolean }>;
  showBanner(options: {
    adUnitId: string;
    position?: "top" | "bottom";
  }): Promise<{ shown: boolean }>;
  hideBanner(): Promise<{ hidden: boolean }>;
  prepareInterstitial(options: { adUnitId: string }): Promise<{ loaded: boolean }>;
  showInterstitial(): Promise<{ shown: boolean }>;
  prepareRewarded(options: { adUnitId: string }): Promise<{ loaded: boolean }>;
  showRewarded(): Promise<{
    shown: boolean;
    reward?: { rewardType: string; amount: number };
  }>;
}

/** How the SDK is obtained. Injectable: the tests load nothing native. */
export type AdMobSdkLoader = () => Promise<AdMobSdk>;

/**
 * Default loader: dynamic `import()` of the Tauri binding. Outside the
 * native runtime the module exists but its `invoke`s fail — that is why the factory
 * NEVER builds this backend on web/desktop (`platform-gate`).
 */
export const defaultAdMobLoader: AdMobSdkLoader = async () => {
  // No type assertion on purpose: if the vendored fork changes a signature,
  // this stops compiling here instead of failing on a child's mobile.
  const sdk: AdMobSdk = await import("@melodimals/admob-api");
  return sdk;
};

/**
 * **Native backend regime floor.** The AdMob SDK only ships in
 * Melodimals, and Melodimals is tier `K`: there is no native binary of this app
 * under a laxer regime. Declaring it here (and not trusting the config that
 * arrives) is what makes the hardening **fail-closed**: if tomorrow someone
 * built the provider with a corrupt config or with tier `A`, the SDK
 * would still initialize in child regime.
 */
export const ADMOB_REGIME_FLOOR_TIER: AdTier = "K";

/** Exact shape of the options `initialize` of the vendored fork accepts. */
export interface AdMobInitOptions {
  tagForChildDirectedTreatment: boolean;
  tagForUnderAgeOfConsent: boolean;
  maxAdContentRating: AdContentRating;
  testDeviceIds?: string[];
}

/**
 * Options with which the native SDK is initialized, hardened in TWO steps:
 *
 *  1. `hardenAdInitOptions` — the caller of `init()` can only harden what
 *     `resolveAdsConfig` already clamped (path 3 of the policy hole: env and
 *     override were the other two);
 *  2. the backend floor (`ADMOB_REGIME_FLOOR_TIER`) — which does not depend on the
 *     config at all.
 *
 * Result in this app: `tagForChildDirectedTreatment`,
 * `tagForUnderAgeOfConsent` and `maxAdContentRating: "G"` are ALWAYS true, and
 * they are so by construction and not by a loose literal. The only thing respected
 * from outside is `testDeviceIds`, which does not affect the policy.
 */
export function admobInitOptions(config: AdsConfig, opts: AdInitOptions = {}): AdMobInitOptions {
  const safe = hardenAdInitOptions(config, opts);
  const floor = AD_TIER_REGIMES[ADMOB_REGIME_FLOOR_TIER];
  const childDirected = safe.childDirected === true || floor.childDirected;
  let rating = strictestAdContentRating(safe.maxAdContentRating ?? "G", floor.maxAdContentRating);
  if (childDirected) {
    rating = strictestAdContentRating(rating, "G");
  }
  return {
    tagForChildDirectedTreatment: childDirected,
    tagForUnderAgeOfConsent: childDirected,
    maxAdContentRating: rating,
    ...(safe.testDeviceIds ? { testDeviceIds: safe.testDeviceIds } : {}),
  };
}

class AdEmitter {
  private readonly listeners = new Map<AdEventName, Set<(e: AdEvent) => void>>();

  on(ev: AdEventName, cb: (e: AdEvent) => void): () => void {
    const set = this.listeners.get(ev) ?? new Set();
    set.add(cb);
    this.listeners.set(ev, set);
    return () => {
      set.delete(cb);
    };
  }

  emit(ev: AdEventName, payload: AdEvent): void {
    for (const cb of this.listeners.get(ev) ?? []) {
      cb(payload);
    }
  }
}

export class AdMobAdProvider implements AdProvider {
  readonly id = AD_NETWORK_ADMOB;
  private readonly emitter = new AdEmitter();
  private sdk?: AdMobSdk;
  private initPromise?: Promise<void>;
  /** Formats with creative already preloaded and ready to show. */
  private readonly loaded = new Set<AdKind>();

  constructor(
    private readonly config: AdsConfig,
    private readonly load: AdMobSdkLoader = defaultAdMobLoader,
  ) {}

  /** The configured block, or Google's test one if there is no real account. */
  private adUnitId(kind: AdKind): string {
    if (kind === "banner" || kind === "interstitial" || kind === "rewarded") {
      return this.config.clientId || ADMOB_TEST_AD_UNITS[kind];
    }
    return "";
  }

  /** Idempotent: several screens can request `init` at once. */
  init(opts: AdInitOptions = {}): Promise<void> {
    this.initPromise ??= (async () => {
      if (!this.config.enabled) {
        return;
      }
      try {
        const sdk = await this.load();
        await sdk.initialize(admobInitOptions(this.config, opts));
        this.sdk = sdk;
      } catch (error) {
        // An ad failure can NEVER break the game: it is left without an SDK and
        // `isEnabled` will return `false` for everything.
        this.emitter.emit("error", {
          kind: "banner",
          placementId: "init",
          error: error instanceof Error ? error.message : "admob init failed",
        });
      }
    })();
    return this.initPromise;
  }

  isEnabled(kind: AdKind): boolean {
    return this.config.enabled && this.sdk !== undefined && SUPPORTED_KINDS.has(kind);
  }

  /**
   * Preload. `false` = no fill ⇒ the UI collapses the slot; never a spinner
   * (even less in front of a child waiting for a drum to sound).
   */
  async prepare(kind: AdKind, _placementId?: string): Promise<boolean> {
    if (!this.isEnabled(kind) || !this.sdk) {
      return false;
    }
    try {
      const adUnitId = this.adUnitId(kind);
      if (kind === "interstitial") {
        const { loaded } = await this.sdk.prepareInterstitial({ adUnitId });
        if (loaded) {
          this.loaded.add(kind);
        }
        return loaded;
      }
      if (kind === "rewarded") {
        const { loaded } = await this.sdk.prepareRewarded({ adUnitId });
        if (loaded) {
          this.loaded.add(kind);
        }
        return loaded;
      }
      // The banner has no separate preload: `showBanner` does it.
      return true;
    } catch {
      return false;
    }
  }

  async show(kind: AdKind, placementId: string): Promise<AdShowResult> {
    if (!this.isEnabled(kind) || !this.sdk) {
      return { shown: false };
    }
    try {
      if (kind === "banner") {
        // Anchored at the bottom: in child regime the banner ALWAYS goes outside the
        // play area, and at the top it would cover the header with the back button.
        const { shown } = await this.sdk.showBanner({
          adUnitId: this.adUnitId(kind),
          position: "bottom",
        });
        if (shown) {
          this.emitter.emit("impression", { kind, placementId });
        }
        return { shown };
      }

      if (kind === "interstitial") {
        if (!this.loaded.has(kind) && !(await this.prepare(kind))) {
          return { shown: false };
        }
        const { shown } = await this.sdk.showInterstitial();
        this.loaded.delete(kind);
        if (shown) {
          this.emitter.emit("impression", { kind, placementId });
          // Preloads the next one in the background, without blocking navigation.
          void this.prepare(kind);
        }
        return { shown };
      }

      if (kind === "rewarded") {
        if (!this.loaded.has(kind) && !(await this.prepare(kind))) {
          return { shown: false };
        }
        const result = await this.sdk.showRewarded();
        this.loaded.delete(kind);
        if (!result.shown) {
          return { shown: false };
        }
        this.emitter.emit("impression", { kind, placementId });
        if (!result.reward) {
          return { shown: true };
        }
        const reward = { type: result.reward.rewardType, amount: result.reward.amount };
        this.emitter.emit("reward", { kind, placementId, reward });
        return { shown: true, reward };
      }

      return { shown: false };
    } catch (error) {
      this.emitter.emit("error", {
        kind,
        placementId,
        error: error instanceof Error ? error.message : "admob show failed",
      });
      return { shown: false };
    }
  }

  /** Removes the banner (when leaving the passive screen or buying premium). */
  async hideBanner(): Promise<void> {
    try {
      await this.sdk?.hideBanner();
    } catch {
      // If it cannot be hidden, there is nothing to do from here.
    }
  }

  on(ev: AdEventName, cb: (e: AdEvent) => void): () => void {
    return this.emitter.on(ev, cb);
  }
}
